/**
 * Resolve free-text person search into name + location/context tags
 * (same query shape as manual Person Search pills).
 */
import { getApiBaseUrl } from '../config/apiBase';
import { splitPersonInlineFocus } from './investigationWorkspaceUtils';

const RO_LOCATION_WORDS = new Set([
  'iasi',
  'iași',
  'bucuresti',
  'bucurești',
  'bucharest',
  'cluj',
  'cluj-napoca',
  'timisoara',
  'timișoara',
  'brasov',
  'brașov',
  'constanta',
  'constanța',
  'craiova',
  'galati',
  'galați',
  'oradea',
  'sibiu',
  'ploiesti',
  'ploiești',
  'bacau',
  'bacău',
  'arad',
  'pitesti',
  'pitești',
  'suceava',
  'romania',
  'românia',
]);

/** Mirrors backend given-name list for compound identity (client fallback). */
const COMMON_GIVEN_NAMES = new Set([
  'marius', 'dan', 'daniel', 'ion', 'ioan', 'andrei', 'alex', 'alexandru',
  'maria', 'ana', 'ioana', 'elena', 'mihai', 'cristian', 'adrian', 'george',
  'vlad', 'radu', 'stefan', 'florin', 'gabriel', 'nicolae', 'constantin',
  'vasile', 'lucian', 'bogdan', 'catalin', 'cătalin', 'ciprian', 'sorin',
  'alina', 'andreea', 'cristina', 'diana', 'irina', 'simona', 'raluca',
  'john', 'jane', 'michael', 'david', 'james', 'robert', 'mary', 'anna',
  'paul', 'peter', 'thomas', 'mark', 'luke', 'sarah', 'emma',
]);

/** Multi-word regions first (longest match), then single-token countries. */
const GEO_PHRASES = [
  ['united kingdom', 'United Kingdom'],
  ['great britain', 'United Kingdom'],
  ['united states', 'United States'],
  ['new zealand', 'New Zealand'],
];

const GEO_SINGLE = {
  romania: 'Romania',
  românia: 'Romania',
  uk: 'United Kingdom',
  england: 'England',
  germany: 'Germany',
  france: 'France',
  italy: 'Italy',
  spain: 'Spain',
  netherlands: 'Netherlands',
  belgium: 'Belgium',
  austria: 'Austria',
  poland: 'Poland',
  hungary: 'Hungary',
  bulgaria: 'Bulgaria',
  usa: 'United States',
};

function isGeoPlaceTerm(term) {
  const t = normalizeWhitespace(term).toLowerCase();
  if (!t) return false;
  if (GEO_SINGLE[t]) return true;
  return GEO_PHRASES.some(([phrase]) => phrase === t);
}

function normalizeWhitespace(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
}

function stripWrappingQuotes(value) {
  let q = normalizeWhitespace(value);
  const pairs = [
    ['"', '"'],
    ["'", "'"],
  ];
  let changed = true;
  while (changed && q) {
    changed = false;
    for (const [openQ, closeQ] of pairs) {
      if (q.length >= 2 && q.startsWith(openQ) && q.endsWith(closeQ)) {
        q = q.slice(openQ.length, -closeQ.length).trim();
        changed = true;
      }
    }
  }
  return q;
}

function titleCasePhrase(phrase) {
  return normalizeWhitespace(phrase)
    .split(' ')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .filter(Boolean)
    .join(' ');
}

function findGeoInText(text) {
  const raw = normalizeWhitespace(text);
  if (!raw) return [];
  const lower = raw.toLowerCase();
  const found = [];
  const seen = new Set();
  for (const [phrase, canonical] of GEO_PHRASES) {
    const re = new RegExp(`\\b${phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (re.test(lower)) {
      const key = canonical.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        found.push(canonical);
      }
    }
  }
  for (const tok of raw.split(/\s+/)) {
    const core = tok.replace(/^[.,;]+|[.,;]+$/g, '');
    const key = GEO_SINGLE[core.toLowerCase()];
    if (key && !seen.has(key.toLowerCase())) {
      seen.add(key.toLowerCase());
      found.push(key);
    }
  }
  return found;
}

function findLocationsInText(text) {
  const raw = normalizeWhitespace(text);
  if (!raw) return [];
  const geo = findGeoInText(raw);
  const tokens = raw.split(/\s+/);
  const found = [...geo];
  const seen = new Set(found.map((x) => x.toLowerCase()));
  for (let i = 0; i < tokens.length; i += 1) {
    const core = tokens[i].replace(/^[.,;]+|[.,;]+$/g, '');
    const lower = core.toLowerCase();
    if (i < tokens.length - 1) {
      const w1 = tokens[i].replace(/^[.,;]+|[.,;]+$/g, '').toLowerCase();
      const w2 = tokens[i + 1].replace(/^[.,;]+|[.,;]+$/g, '').toLowerCase();
      if (w1 === 'cluj' && w2 === 'napoca') {
        const phrase = `${tokens[i]} ${tokens[i + 1]}`;
        const key = 'cluj napoca';
        if (!seen.has(key)) {
          seen.add(key);
          found.push(titleCasePhrase(phrase));
        }
        i += 1;
        continue;
      }
    }
    if (RO_LOCATION_WORDS.has(lower) && !seen.has(lower)) {
      seen.add(lower);
      found.push(titleCasePhrase(core));
    }
  }
  return found;
}

function stripLocationsAndFocusFromText(fullText, locationTags, contextTags) {
  const remove = new Set();
  for (const phrase of [...(locationTags || []), ...(contextTags || [])]) {
    for (const part of normalizeWhitespace(phrase).split(/\s+/)) {
      if (part) remove.add(part.toLowerCase());
    }
  }
  const kept = [];
  for (const tok of normalizeWhitespace(fullText).split(/\s+/)) {
    const core = tok.replace(/^[.,;]+|[.,;]+$/g, '');
    if (!core) continue;
    if (remove.has(core.toLowerCase())) continue;
    kept.push(core);
  }
  return kept.join(' ');
}

function mergeUniqueTags(...lists) {
  const out = [];
  const seen = new Set();
  for (const list of lists) {
    for (const item of list || []) {
      const t = normalizeWhitespace(item);
      if (!t) continue;
      const key = t.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(titleCasePhrase(t));
    }
  }
  return out;
}

function inferPersonNameAndFocus(fullText, locationTags, explicitFocus = []) {
  const locSet = new Set();
  for (const loc of locationTags || []) {
    for (const part of normalizeWhitespace(loc).split(/\s+/)) {
      if (part) locSet.add(part.toLowerCase());
    }
  }
  const tokens = [];
  for (const tok of normalizeWhitespace(fullText).split(/\s+/)) {
    const core = tok.replace(/^[.,;]+|[.,;]+$/g, '');
    if (!core) continue;
    if (locSet.has(core.toLowerCase())) continue;
    if (RO_LOCATION_WORDS.has(core.toLowerCase())) continue;
    tokens.push(core);
  }
  if (tokens.length <= 2) {
    return { name: tokens.join(' '), focusPhrase: '' };
  }
  let nameParts = tokens.slice(0, 2);
  let tailParts = tokens.slice(2);
  // Fold contiguous known prenume into identity (Sarb Mihai Vlad).
  const leftover = [];
  let absorbing = true;
  for (const part of tailParts) {
    const low = part.toLowerCase();
    if (absorbing && COMMON_GIVEN_NAMES.has(low)) {
      nameParts.push(part);
      continue;
    }
    absorbing = false;
    leftover.push(part);
  }
  const name = nameParts.join(' ');
  const tail = leftover.join(' ');
  const isFocusTail =
    leftover.length >= 2 ||
    (leftover.length === 1 && leftover[0].replace(/[^A-Za-z0-9]/g, '').length >= 4);
  const focusPhrase = isFocusTail && !isGeoPlaceTerm(tail) ? tail : '';
  if (explicitFocus.length) {
    return { name, focusPhrase: '' };
  }
  return { name, focusPhrase };
}

function resolveClientSide(text) {
  const { input: identityPart, contextTags: explicitFocus } = splitPersonInlineFocus(text);
  const full = normalizeWhitespace(text);
  const identity = normalizeWhitespace(identityPart) || full;

  const locationsInText = findLocationsInText(full);
  const locationTags = mergeUniqueTags(findLocationsInText(identity), locationsInText);

  const { name, focusPhrase } = inferPersonNameAndFocus(identity, locationTags, explicitFocus);
  const contextTags = mergeUniqueTags(
    explicitFocus,
    focusPhrase ? [focusPhrase] : [],
  ).filter((t) => !isGeoPlaceTerm(t));

  return {
    name: name || stripLocationsAndFocusFromText(full, locationTags, contextTags) || full,
    locationTags: mergeUniqueTags(locationTags, findGeoInText(full)),
    contextTags,
    username: null,
  };
}

async function fetchSearchPreprocess(text) {
  const base = getApiBaseUrl();
  // Must stay below user-visible “stuck GO” threshold. Backend Ollama budget is ~1.5s;
  // spaCy-only responses should return well under this window.
  const timeoutMs = 2500;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${base}/search/preprocess`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data || typeof data !== 'object') return null;
    return data;
  } catch {
    // Timeout/abort/network → client-side parsing (never block SSE on AI preprocess).
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * @param {string} rawQuery - full search bar text
 * @param {{ locationTags?: string[], contextTags?: string[] }} [existing]
 * @returns {Promise<{ name: string, locationTags: string[], contextTags: string[], username: string|null, scanIntent?: string, suggestedTab?: string, preprocessSource?: string }>}
 */
export async function resolvePersonSearchFromText(rawQuery, existing = {}) {
  const text = stripWrappingQuotes(rawQuery);
  if (!text) {
    return { name: '', locationTags: [], contextTags: [], username: null, scanIntent: 'light' };
  }

  let preprocess = null;
  try {
    preprocess = await fetchSearchPreprocess(text);
  } catch {
    preprocess = null;
  }

  if (preprocess?.identity) {
    const streamParams = preprocess.stream_params && typeof preprocess.stream_params === 'object'
      ? preprocess.stream_params
      : {};
    const hybrid = preprocess.hybrid && typeof preprocess.hybrid === 'object'
      ? preprocess.hybrid
      : null;
    const companyDomain = String(
      streamParams.domain || streamParams.primary_domain || '',
    ).trim() || null;
    const displayName = normalizeWhitespace(
      streamParams.name || hybrid?.stream_name || preprocess.identity,
    );
    // Propagate classified context from backend preprocess.
    const contextCompany = String(preprocess.context_company || streamParams.context_focus || '').trim();
    const contextUsername = String(preprocess.context_username || streamParams.context_username || '').trim();

    return {
      name: displayName,
      locationTags: mergeUniqueTags(existing.locationTags, preprocess.location_tags || []),
      contextTags: mergeUniqueTags(existing.contextTags, preprocess.context_tags || []),
      username: contextUsername || null,
      contextCompany: contextCompany || null,
      scanIntent: String(preprocess.scan_intent || 'light').toLowerCase(),
      suggestedTab: preprocess.suggested_tab || 'person',
      preprocessSource: preprocess.source || 'spacy',
      streamParams,
      hybrid,
      companyDomain,
    };
  }

  // Instant client fallback — do not call /parse/person (adds another spaCy round-trip
  // after a timed-out preprocess and was the ~2s second hop in admin timing traces).
  const client = resolveClientSide(text);
  return {
    name: client.name,
    locationTags: mergeUniqueTags(existing.locationTags, client.locationTags),
    contextTags: mergeUniqueTags(existing.contextTags, client.contextTags),
    username: client.username,
    scanIntent: 'light',
    suggestedTab: 'person',
    preprocessSource: 'client',
  };
}

/**
 * Map a resolved person query into SSE params (no HTTP).
 *
 * @param {{ name: string, locationTags?: string[], contextTags?: string[], scanIntent?: string }} resolved
 * @param {{ scanType?: string, searchScope?: string, usernameTags?: string[] }} [personTabState]
 * @returns {Record<string, string>}
 */
export function buildPersonStreamParamsFromResolved(resolved, personTabState = {}) {
  const scanType =
    personTabState.scanType ||
    (resolved.scanIntent === 'deep' ? 'deep' : 'light');
  const params = {
    name: resolved.name,
    scope: personTabState.searchScope === 'global' ? 'global' : 'ro',
  };
  if (scanType === 'deep') {
    params.scan_type = 'deep';
  }
  if (resolved.locationTags?.length) {
    params.context_locations = resolved.locationTags.join(',');
  }
  if (resolved.contextTags?.length) {
    params.context_focus = resolved.contextTags.join(',');
  }
  // Classified company context from preprocess overrides generic context_focus for
  // targeted employer dorking in aggregate_name.
  if (resolved.contextCompany && !params.context_focus) {
    params.context_focus = resolved.contextCompany;
  }
  // Username from classifier or from preprocess stream_params.
  if (resolved.username && !personTabState.usernameTags?.length) {
    params.context_username = resolved.username;
  }
  if (personTabState.usernameTags?.length) {
    params.context_username = personTabState.usernameTags.join(',');
  }
  return params;
}

/**
 * True when handleSearch can reuse the hero GO preprocess result (same identity).
 *
 * @param {{ params?: { name?: string }, resolved?: { name?: string } } | null} prefetch
 * @param {string} currentInput
 * @returns {boolean}
 */
export function shouldReusePersonPreprocess(prefetch, currentInput) {
  if (!prefetch?.params || !prefetch?.resolved) return false;
  const prefetchName = String(prefetch.resolved.name || prefetch.params.name || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  const current = String(currentInput || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  return Boolean(prefetchName) && prefetchName === current;
}

/**
 * Build /aggregate-stream-name query params (same shape as manual location + context pills).
 */
export async function buildPersonStreamParams(input, personTabState = {}) {
  const resolved = await resolvePersonSearchFromText(input, {
    locationTags: personTabState.locationTags,
    contextTags: personTabState.contextTags,
  });
  return {
    params: buildPersonStreamParamsFromResolved(resolved, personTabState),
    resolved,
  };
}
